import { UnknownElementError } from '@/features/legends/editor/legend-document'
import { BoolQuestionEditorViewModel } from '@/features/legends/questions/types/BoolQuestionEditorViewModel'
import { BreakerQuestionEditorViewModel } from '@/features/legends/questions/types/BreakerQuestionEditorViewModel'
import { DateTimeQuestionEditorViewModel } from '@/features/legends/questions/types/DateTimeQuestionEditorViewModel'
import { DocumentsQuestionEditorViewModel } from '@/features/legends/questions/types/DocumentsQuestionEditorViewModel'
import { EffectQuestionEditorViewModel } from '@/features/legends/questions/types/EffectQuestionEditorViewModel'
import { InventoryItemQuestionEditorViewModel } from '@/features/legends/questions/types/InventoryItemQuestionEditorViewModel'
import { NumericQuestionEditorViewModel } from '@/features/legends/questions/types/NumericQuestionEditorViewModel'
import { PathQuestionEditorViewModel } from '@/features/legends/questions/types/PathQuestionEditorViewModel'
import { PersonalOrganizerCollectedValueQuestionEditorViewModel } from '@/features/legends/questions/types/PersonalOrganizerCollectedValueQuestionEditorViewModel'
import { PersonalOrganizerPrefillQuestionEditorViewModel } from '@/features/legends/questions/types/PersonalOrganizerPrefillQuestionEditorViewModel'
import { SelectQuestionEditorViewModel } from '@/features/legends/questions/types/SelectQuestionEditorViewModel'
import { TextQuestionEditorViewModel } from '@/features/legends/questions/types/TextQuestionEditorViewModel'
import { VariableQuestionEditorViewModel } from '@/features/legends/questions/types/VariableQuestionEditorViewModel'
import type {
  QuestionEditorViewModelBase,
  QuestionEditorViewModelOptions,
} from './QuestionEditorViewModelBase'
import type { QuestionType } from './question-types'
import type { Question } from './questions'

/**
 * Any question editor, narrowed by its `type`.
 */
export type AnyQuestionEditor =
  | BoolQuestionEditorViewModel
  | BreakerQuestionEditorViewModel
  | DateTimeQuestionEditorViewModel
  | DocumentsQuestionEditorViewModel
  | EffectQuestionEditorViewModel
  | InventoryItemQuestionEditorViewModel
  | NumericQuestionEditorViewModel
  | PathQuestionEditorViewModel
  | PersonalOrganizerCollectedValueQuestionEditorViewModel
  | PersonalOrganizerPrefillQuestionEditorViewModel
  | SelectQuestionEditorViewModel
  | TextQuestionEditorViewModel
  | VariableQuestionEditorViewModel

/**
 * The question as the union of the editor types, so it narrows by `type`.
 */
export function asAnyQuestionEditor(
  question: QuestionEditorViewModelBase,
): AnyQuestionEditor {
  // SAFETY: questions are only created by `createQuestion`, which returns
  // the union, and every subclass of `QuestionEditorViewModelBase` is in it.
  return question as AnyQuestionEditor
}

/**
 * Creates a new question of the type.
 *
 * Kept as a function rather than a map of classes: paths import the builder,
 * which imports this module, so the classes may not be defined yet when this
 * module loads.
 */
export function createQuestion(
  type: QuestionType,
  options: QuestionEditorViewModelOptions,
): AnyQuestionEditor {
  switch (type) {
    case 'Bool':
      return new BoolQuestionEditorViewModel(options)
    case 'Breaker':
      return new BreakerQuestionEditorViewModel(options)
    case 'DateTime':
      return new DateTimeQuestionEditorViewModel(options)
    case 'Documents':
      return new DocumentsQuestionEditorViewModel(options)
    case 'Effect':
      return new EffectQuestionEditorViewModel(options)
    case 'InventoryItem':
      return new InventoryItemQuestionEditorViewModel(options)
    case 'Numeric':
      return new NumericQuestionEditorViewModel(options)
    case 'Path':
      return new PathQuestionEditorViewModel(options)
    case 'PersonalOrganizerCollectedValue':
      return new PersonalOrganizerCollectedValueQuestionEditorViewModel(options)
    case 'PersonalOrganizerPrefill':
      return new PersonalOrganizerPrefillQuestionEditorViewModel(options)
    case 'Select':
      return new SelectQuestionEditorViewModel(options)
    case 'Text':
      return new TextQuestionEditorViewModel(options)
    case 'Variable':
      return new VariableQuestionEditorViewModel(options)
  }
}

/**
 * Creates the editor for an element of the legend document. Throws for an
 * element, or a number format, of a type the builder doesn't know, which no
 * editor can show.
 */
export function hydrateQuestion(
  question: Question,
  options: QuestionEditorViewModelOptions,
): AnyQuestionEditor {
  if (question.type === 'Unknown') {
    throw new UnknownElementError('question', question)
  }
  const editor = createQuestion(question.type, options)
  // SAFETY: the editor was created for the question's own type.
  ;(editor as { hydrate(question: Question): void }).hydrate(question)
  return editor
}
